import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronRightIcon, Cross2Icon, ReaderIcon, ReloadIcon } from '@radix-ui/react-icons';
import type { EbookClient } from '../services/ebooks';
import { loadClassroomTextbook, type ClassroomTextbook } from '../services/classroom-textbooks';
import './classroom-textbooks.css';

export function ClassroomTextbookPicker({
  client,
  onSelect,
  onClose,
}: {
  readonly client: Pick<EbookClient, 'list' | 'document'>;
  readonly onSelect: (textbook: ClassroomTextbook) => void;
  readonly onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const active = useRef(false);
  const selectionRequest = useRef(0);
  const headingId = useId();
  const searchId = useId();
  const [books, setBooks] = useState<Awaited<ReturnType<EbookClient['list']>>>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [listError, setListError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const [failedId, setFailedId] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    active.current = true;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => {
      active.current = false;
      selectionRequest.current += 1;
      dialog?.close();
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    selectionRequest.current += 1;
    setLoadingId(null);
    setSelectionError('');
    setFailedId(null);
    setLoading(true);
    setListError('');
    void client.list().then(
      (value) => {
        if (cancelled) return;
        setBooks(value);
        setLoading(false);
      },
      (reason: unknown) => {
        if (cancelled) return;
        setListError(userErrorMessage(reason, '教材列表加载失败，请重试。'));
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
      selectionRequest.current += 1;
    };
  }, [client, retry]);

  const select = async (id: string) => {
    const request = ++selectionRequest.current;
    setLoadingId(id);
    setSelectionError('');
    setFailedId(null);
    try {
      const textbook = await loadClassroomTextbook(client, id);
      if (!active.current || request !== selectionRequest.current) return;
      setLoadingId(null);
      onSelect(textbook);
    } catch (reason: unknown) {
      if (!active.current || request !== selectionRequest.current) return;
      setLoadingId(null);
      setFailedId(id);
      setSelectionError(userErrorMessage(reason, '教材题目加载失败，请重试。'));
    }
  };
  const close = () => {
    selectionRequest.current += 1;
    onClose();
  };
  const query = search.trim().toLocaleLowerCase();
  const visibleBooks = books.filter((book) => book.name.toLocaleLowerCase().includes(query));

  return (
    <dialog
      ref={dialogRef}
      className="studio-textbook-picker"
      aria-labelledby={headingId}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <header className="studio-textbook-picker-heading">
        <div>
          <h2 id={headingId}>教材题目</h2>
          <p>选择教材，查看已加入编排的题目。</p>
        </div>
        <button
          type="button"
          className="studio-textbook-icon-button"
          aria-label="关闭教材列表"
          onClick={close}
        >
          <Cross2Icon aria-hidden="true" />
        </button>
      </header>
      <div className="studio-textbook-picker-body">
        <label className="studio-textbook-search" htmlFor={searchId}>
          <span>搜索教材</span>
          <input
            id={searchId}
            type="search"
            placeholder="输入教材名称"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </label>
        {selectionError ? (
          <div className="studio-textbook-error" role="alert">
            <p>{selectionError}</p>
            {failedId ? (
              <button type="button" onClick={() => void select(failedId)}>
                <ReloadIcon aria-hidden="true" />
                重新加载题目
              </button>
            ) : null}
          </div>
        ) : null}
        {loading ? (
          <p className="studio-textbook-state" role="status">
            正在加载教材列表…
          </p>
        ) : listError ? (
          <div className="studio-textbook-error" role="alert">
            <p>{listError}</p>
            <button type="button" onClick={() => setRetry((value) => value + 1)}>
              <ReloadIcon aria-hidden="true" />
              重试
            </button>
          </div>
        ) : books.length === 0 ? (
          <p className="studio-textbook-state">暂无教材，请先在“我的教材”中导入教材并编排题目。</p>
        ) : visibleBooks.length === 0 ? (
          <p className="studio-textbook-state" role="status">
            未找到匹配的教材，请更换关键词。
          </p>
        ) : (
          <ul
            className="studio-textbook-book-list"
            aria-label="教材列表"
            aria-busy={loadingId !== null}
          >
            {visibleBooks.map((book) => (
              <li key={book.id}>
                <button
                  type="button"
                  disabled={loadingId !== null}
                  onClick={() => void select(book.id)}
                >
                  <ReaderIcon aria-hidden="true" />
                  <span>
                    <strong>{book.name}</strong>
                    <small>PDF 共 {book.pageCount} 页</small>
                  </span>
                  {loadingId === book.id ? (
                    <small>正在加载…</small>
                  ) : (
                    <ChevronRightIcon aria-hidden="true" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {loadingId !== null ? (
          <p className="studio-textbook-loading" role="status">
            正在读取教材编排，请稍候…
          </p>
        ) : null}
      </div>
    </dialog>
  );
}
